import React, { useState } from 'react';
import { X, CheckCircle, AlertTriangle, ArrowRight, ArrowLeft, Building2, Users, Euro, Briefcase, FileText } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';
import { NIS2_DATA, SPECIAL_SERVICES } from '../data/nis2Data';

const Nis2Widget = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    employees: null,
    turnover: null,
    balance: null,
    sector: '',
    service: '',
    specialStatus: false
  });
  const [result, setResult] = useState(null);
  const [availableServices, setAvailableServices] = useState([]);

  // Determine size class
  const determineSizeClass = (employees, turnover, balance) => {
    const emp = Number(employees);
    const turn = Number(turnover) * 1000000;
    const bal = Number(balance) * 1000000;
    
    const thr = NIS2_DATA.thresholds;
    
    if (emp >= thr.medium.max_employees || 
        (turn > thr.medium.max_turnover && bal > thr.medium.max_balance_sheet)) {
      return 'large';
    }
    
    if (emp >= thr.small.max_employees || 
        (turn > thr.small.max_turnover && bal > thr.small.max_balance_sheet)) {
      return 'medium';
    }
    
    if (emp >= thr.micro.max_employees || 
        (turn > thr.micro.max_turnover && bal > thr.micro.max_balance_sheet)) {
      return 'small';
    }
    
    return 'micro';
  };

  // Determine assignment
  const determineAssignment = (sizeClass, sector, service, specialStatus) => {
    if (specialStatus) {
      return 'essential';
    }
    
    if (SPECIAL_SERVICES.always_essential.includes(service)) {
      return 'essential';
    }
    
    if (sizeClass === 'micro' || sizeClass === 'small') {
      return 'outside_scope';
    }
    
    const sectorObj = NIS2_DATA.sectorsData.find(s => s.sector === sector);
    let sectorCategory = sectorObj ? sectorObj.category : null;
    
    if (SPECIAL_SERVICES.annex_ii_digital.includes(service)) {
      sectorCategory = 'important';
    }
    
    if (sizeClass === 'medium' && SPECIAL_SERVICES.medium_as_essential.includes(service)) {
      return 'essential';
    }
    
    if (sectorCategory === 'essential') {
      if (sizeClass === 'large') {
        return 'essential';
      } else {
        return 'important';
      }
    }
    
    if (sectorCategory === 'important') {
      return 'important';
    }
    
    return 'outside_scope';
  };

  // Get size class name
  const getSizeClassName = (sizeClass) => {
    const names = {
      'micro': 'mikropodnik',
      'small': 'malý podnik',
      'medium': 'střední podnik',
      'large': 'velký podnik'
    };
    return names[sizeClass] || sizeClass;
  };

  // Generate reasons
  const generateReasons = (assignment, sizeClass, sector, service, specialStatus, employees, turnover, balance) => {
    const reasons = [];
    
    if (specialStatus) {
      reasons.push('Organizace má speciální status kritického poskytovatele, proto je zařazena mezi základní subjekty bez ohledu na velikost.');
      return reasons;
    }
    
    if (SPECIAL_SERVICES.always_essential.includes(service)) {
      reasons.push(`Poskytovaná služba ("${service}") je automaticky zařazena mezi základní subjekty bez ohledu na velikost organizace.`);
      return reasons;
    }
    
    if (assignment === 'outside_scope') {
      reasons.push(`Organizace je ${getSizeClassName(sizeClass)} a nesplňuje kritéria pro zařazení do působnosti NIS2.`);
      reasons.push('Mikropodniky a malé podniky obecně nespadají do regulace NIS2.');
      return reasons;
    }
    
    const emp = Number(employees);
    const turn = Number(turnover);
    const bal = Number(balance);
    
    if (sizeClass === 'large') {
      if (emp >= 250) {
        reasons.push(`Organizace má ${emp} zaměstnanců, což odpovídá velkému podniku (≥ 250 zaměstnanců).`);
      } else {
        const turnCzk = Math.round(turn * 24);
        const balCzk = Math.round(bal * 24);
        const turnText = turnCzk >= 1000 ? '1 mld. CZK' : `${turnCzk} mil. CZK`;
        const balText = balCzk >= 1000 ? '1 mld. CZK' : `${balCzk} mil. CZK`;
        reasons.push(`Organizace má ${emp} zaměstnanců, ale obrat (${turnText}) a bilanční suma (${balText}) odpovídají velkému podniku.`);
      }
    } else if (sizeClass === 'medium') {
      if (emp >= 50) {
        reasons.push(`Organizace má ${emp} zaměstnanců, což odpovídá střednímu podniku (50-249 zaměstnanců).`);
      } else {
        const turnCzk = Math.round(turn * 24);
        const balCzk = Math.round(bal * 24);
        const turnText = turnCzk >= 1000 ? '1 mld. CZK' : `${turnCzk} mil. CZK`;
        const balText = balCzk >= 1000 ? '1 mld. CZK' : `${balCzk} mil. CZK`;
        reasons.push(`Organizace má ${emp} zaměstnanců, ale obrat (${turnText}) a/nebo bilanční suma (${balText}) odpovídají střednímu podniku.`);
      }
    }
    
    const sectorObj = NIS2_DATA.sectorsData.find(s => s.sector === sector);
    const isEssentialSector = sectorObj && sectorObj.category === 'essential';
    const isImportantSector = sectorObj && sectorObj.category === 'important';
    const isAnnexIIDigital = SPECIAL_SERVICES.annex_ii_digital.includes(service);
    
    if (assignment === 'essential') {
      if (SPECIAL_SERVICES.medium_as_essential.includes(service) && sizeClass === 'medium') {
        reasons.push('Poskytovatel veřejných telekomunikačních služeb je dle směrnice NIS2 zařazen mezi základní subjekty již od střední velikosti.');
      } else if (isEssentialSector && sizeClass === 'large') {
        reasons.push(`Odvětví "${sector}" patří mezi vysoce kritická (Annex I). Velké podniky v těchto odvětvích jsou základní subjekty.`);
      }
    } else if (assignment === 'important') {
      if (isAnnexIIDigital) {
        reasons.push(`Služba "${service}" je řazena mezi ostatní kritická odvětví (Annex II). Střední a velké podniky poskytující tuto službu jsou důležité subjekty.`);
      } else if (isEssentialSector && sizeClass === 'medium') {
        reasons.push(`Odvětví "${sector}" patří mezi vysoce kritická, ale jako střední podnik jste zařazeni mezi důležité subjekty (nikoli základní).`);
      } else if (isImportantSector) {
        reasons.push(`Odvětví "${sector}" patří mezi ostatní kritická odvětví (Annex II). Střední a velké podniky v těchto odvětvích jsou důležité subjekty.`);
      }
    }
    
    return reasons;
  };

  const handleSectorChange = (sectorValue) => {
    setFormData({ ...formData, sector: sectorValue, service: '' });
    
    const sectorObj = NIS2_DATA.sectorsData.find(s => s.sector === sectorValue);
    if (sectorObj) {
      setAvailableServices(sectorObj.services);
    } else {
      setAvailableServices([]);
    }
  };

  const handleSubmit = () => {
    // Validate
    if (!formData.employees || !formData.turnover || !formData.balance) {
      alert('Prosím, vyberte všechny povinné údaje o velikosti podniku.');
      return;
    }
    
    if (!formData.sector || !formData.service) {
      alert('Prosím, vyberte odvětví i poskytovanou službu.');
      return;
    }
    
    // Calculate
    const sizeClass = determineSizeClass(
      formData.employees,
      formData.turnover,
      formData.balance
    );
    
    const assignment = determineAssignment(
      sizeClass,
      formData.sector,
      formData.service,
      formData.specialStatus
    );
    
    const reasons = generateReasons(
      assignment,
      sizeClass,
      formData.sector,
      formData.service,
      formData.specialStatus,
      formData.employees,
      formData.turnover,
      formData.balance
    );
    
    setResult({ assignment, sizeClass, reasons });
  };

  const resetWidget = () => {
    setFormData({
      employees: null,
      turnover: null,
      balance: null,
      sector: '',
      service: '',
      specialStatus: false
    });
    setResult(null);
    setAvailableServices([]);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm overflow-y-auto">
      <div className="bg-white section-rounded shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto my-8">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-2xl font-bold text-[#101648]">Týká se mě NIS2?</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {!result ? (
            <div className="space-y-6">
              <p className="text-gray-600">
                Vyplňte několik údajů a zjistěte, zda se na vás pravděpodobně vztahuje směrnice NIS2. 
                Dotazník je orientační – ukáže, zda pravděpodobně spadáte pod zákon, a nabídne vám další kroky.
              </p>

              {/* Employees */}
              <div>
                <label className="block text-sm font-semibold text-[#101648] mb-3">
                  Počet zaměstnanců <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { value: 5, label: '0–9' },
                    { value: 30, label: '10–49' },
                    { value: 150, label: '50–249' },
                    { value: 300, label: '250+' }
                  ].map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, employees: opt.value })}
                      className={`p-3 text-sm font-medium section-rounded border-2 transition-all ${
                        formData.employees === opt.value
                          ? 'border-[#3929c5] bg-[#e4e8ff] text-[#3929c5]'
                          : 'border-gray-200 bg-white text-gray-700 hover:border-[#3929c5]'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <small className="block mt-2 text-gray-500">Vyberte kategorii počtu zaměstnanců (Annual Work Units).</small>
              </div>

              {/* Turnover */}
              <div>
                <label className="block text-sm font-semibold text-[#101648] mb-3">
                  Roční obrat <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { value: 1, label: '≤ 48 mil. CZK' },
                    { value: 5, label: '≤ 240 mil. CZK' },
                    { value: 30, label: '≤ 1 mld. CZK' },
                    { value: 60, label: '> 1 mld. CZK' }
                  ].map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, turnover: opt.value })}
                      className={`p-3 text-sm font-medium section-rounded border-2 transition-all ${
                        formData.turnover === opt.value
                          ? 'border-[#3929c5] bg-[#e4e8ff] text-[#3929c5]'
                          : 'border-gray-200 bg-white text-gray-700 hover:border-[#3929c5]'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <small className="block mt-2 text-gray-500">Vyberte kategorii ročního obratu za poslední účetní období.</small>
              </div>

              {/* Balance Sheet */}
              <div>
                <label className="block text-sm font-semibold text-[#101648] mb-3">
                  Roční bilanční suma (rozvaha) <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { value: 1, label: '≤ 48 mil. CZK' },
                    { value: 5, label: '≤ 240 mil. CZK' },
                    { value: 25, label: '≤ 1 mld. CZK' },
                    { value: 50, label: '> 1 mld. CZK' }
                  ].map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, balance: opt.value })}
                      className={`p-3 text-sm font-medium section-rounded border-2 transition-all ${
                        formData.balance === opt.value
                          ? 'border-[#3929c5] bg-[#e4e8ff] text-[#3929c5]'
                          : 'border-gray-200 bg-white text-gray-700 hover:border-[#3929c5]'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <small className="block mt-2 text-gray-500">Vyberte kategorii celkové roční bilanční sumy (aktiva).</small>
              </div>

              {/* Sector */}
              <div>
                <label className="block text-sm font-semibold text-[#101648] mb-3">
                  Odvětví poskytované služby <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.sector}
                  onChange={(e) => handleSectorChange(e.target.value)}
                  className="w-full p-3 border-2 border-gray-200 section-rounded focus:border-[#3929c5] focus:ring-0 outline-none"
                >
                  <option value="">-- Vyberte odvětví --</option>
                  {NIS2_DATA.sectorsData.map(sector => (
                    <option key={sector.sector} value={sector.sector}>{sector.sector}</option>
                  ))}
                </select>
                <small className="block mt-2 text-gray-500">Vyberte odvětví, do kterého spadá poskytovaná služba.</small>
              </div>

              {/* Service */}
              <div>
                <label className="block text-sm font-semibold text-[#101648] mb-3">
                  Poskytovaná služba <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.service}
                  onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                  disabled={!formData.sector}
                  className="w-full p-3 border-2 border-gray-200 section-rounded focus:border-[#3929c5] focus:ring-0 outline-none disabled:bg-gray-100"
                >
                  <option value="">
                    {formData.sector ? '-- Vyberte službu --' : '-- Nejprve vyberte odvětví --'}
                  </option>
                  {availableServices.map(service => (
                    <option key={service} value={service}>{service}</option>
                  ))}
                </select>
                <small className="block mt-2 text-gray-500">Vyberte konkrétní službu, kterou poskytujete.</small>
              </div>

              {/* Special Status */}
              <div className="bg-[#fff8ea] section-rounded p-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.specialStatus}
                    onChange={(e) => setFormData({ ...formData, specialStatus: e.target.checked })}
                    className="mt-1 w-5 h-5 text-[#3929c5] border-gray-300 rounded focus:ring-[#3929c5]"
                  />
                  <div>
                    <span className="font-medium text-[#101648]">Mám zvláštní význam pro stát nebo klíčovou službu</span>
                    <p className="text-sm text-gray-600 mt-1">
                      Týká se subjektů s nezastupitelnou úlohou — například jediný poskytovatel v ČR, 
                      správce klíčové infrastruktury nebo kvalifikovaný poskytovatel služeb vytvářejících důvěru.
                    </p>
                  </div>
                </label>
              </div>

              {/* Submit */}
              <Button
                onClick={handleSubmit}
                className="w-full bg-[#3929c5] hover:bg-[#2d1f9f] text-white btn-rounded px-8 py-6 text-lg font-semibold"
              >
                Vyhodnotit
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          ) : (
            /* Result */
            <div className="space-y-6">
              <div className={`p-6 section-rounded border-2 ${
                result.assignment === 'essential'
                  ? 'bg-red-50 border-red-200'
                  : result.assignment === 'important'
                  ? 'bg-orange-50 border-orange-200'
                  : 'bg-green-50 border-green-200'
              }`}>
                <div className="flex items-start gap-3 mb-4">
                  {result.assignment === 'outside_scope' ? (
                    <CheckCircle className="w-8 h-8 text-green-600 flex-shrink-0" />
                  ) : (
                    <AlertTriangle className={`w-8 h-8 flex-shrink-0 ${
                      result.assignment === 'essential' ? 'text-red-600' : 'text-orange-600'
                    }`} />
                  )}
                  <div>
                    <h3 className="text-2xl font-bold text-[#101648] mb-2">
                      {result.assignment === 'essential' ? 'Základní subjekt' :
                       result.assignment === 'important' ? 'Důležitý subjekt' :
                       'Mimo působnost NIS2'}
                    </h3>
                    <p className="text-gray-700">
                      {result.assignment === 'outside_scope'
                        ? 'Vaše organizace pravděpodobně nespadá pod NIS2.'
                        : 'Doporučujeme ověřit vaše povinnosti podle zákona o kybernetické bezpečnosti.'}
                    </p>
                  </div>
                </div>

                <div className="mt-6">
                  <h4 className="font-semibold text-[#101648] mb-3">Důvody zařazení:</h4>
                  <ul className="space-y-2">
                    {result.reasons.map((reason, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-[#3929c5] flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{reason}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* CTA */}
              <div className="bg-[#3929c5] section-rounded p-6 text-white">
                <h4 className="text-xl font-bold mb-2">Potřebujete pomoc s NIS2?</h4>
                <p className="mb-4 opacity-90">
                  Spojte se s námi a proberme vaše možnosti.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a 
                    href="https://meetings.hubspot.com/vit-ulicny/nis2?uuid=securecheck-8"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1"
                  >
                    <Button className="w-full bg-white text-[#3929c5] hover:bg-gray-100 btn-rounded">
                      Spojit se s námi
                    </Button>
                  </a>
                  <Button
                    onClick={resetWidget}
                    variant="outline"
                    className="flex-1 border-2 border-white text-white hover:bg-white hover:text-[#3929c5] btn-rounded"
                  >
                    Nový test
                  </Button>
                </div>
              </div>

              <p className="text-sm text-gray-500 text-center">
                Dotazník je pouze orientační, nemusí být přesný. Slouží k ilustraci našeho řešení.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Nis2Widget;